import Button, { ButtonType } from '../../../../../ui/Button/BlockButton';
import { useTheme } from '../../../../../../hooks/useTheme';
import styles from '../Fetch.module.css';
import d from '../FetchDesign.module.css';
import { useFetchDesign, type FetchDesign } from '../fetchDesign';
import { FetchOfferBar } from './FetchOfferBar';
import { FetchLogo } from './FetchLogo';
import { FetchTrustpilot } from './FetchTrustpilot';

interface FetchTeaserProps {
  onGetCovered: () => void;
  onNoThanks: () => void;
  isMobile?: boolean;
  inline?: boolean;
}

// "See My Price" won the Oct 2026 CTA test and is now the CTA in
// every arm of rm-fetch-design, control included. The copy is identical
// across arms so the test measures the design and nothing else.
const CTA_LABEL = 'See My Price';
const MOBILE_CTA_LABEL = 'See my price';
const TEASER_SUB =
  'Plans for dogs and cats, priced to fit your budget. Use any vet in the U.S. or Canada.';

const QUOTE_NOTE = 'Free quote in about a minute';

interface ArmProps {
  design: FetchDesign;
  onGetCovered: () => void;
  onNoThanks: () => void;
  bodyFont?: string;
  headerFont?: string;
}

function TreatmentDesktop({
  design,
  onGetCovered,
  onNoThanks,
  bodyFont,
  headerFont,
}: ArmProps) {
  const copy = (
    <>
      <h3 className={d.title} style={{ fontFamily: headerFont }}>
        {design.heading}
      </h3>
      <p className={d.sub}>{TEASER_SUB}</p>
      {design.trustpilot && <FetchTrustpilot />}
      <div className={d.actions}>
        <Button type={ButtonType.button} onClick={onGetCovered}>
          {CTA_LABEL}
        </Button>
        <button type="button" className={d.decline} onClick={onNoThanks}>
          No thanks
        </button>
      </div>
      <p className={`${d.quoteNote} ${d.quoteNoteLeft}`}>{QUOTE_NOTE}</p>
    </>
  );

  if (design.layout === 't1') {
    return (
      <div
        className={`${styles.teaserCard} ${d.t1Card}`}
        style={{ fontFamily: bodyFont }}
      >
        <div
          className={`${d.teaserArt} ${d.t1Art}`}
          data-hero={design.hero}
          aria-hidden
        />
        <div className={d.t1Main}>
          <div className={d.t1Copy}>
            <div className={d.chips}>
              <span className={styles.sponsoredChip}>Sponsored</span>
              <span className={styles.sponsoredChip}>For Pet Parents</span>
            </div>
            {copy}
          </div>
          <div className={d.logoCard}>
            <FetchLogo />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.teaserCard} style={{ fontFamily: bodyFont }}>
      <FetchOfferBar />
      <div className={d.t2Body}>
        <div className={d.logoCard}>
          <FetchLogo />
        </div>
        <div className={d.t2Copy}>
          <span className={styles.sponsoredChip}>Sponsored</span>
          {copy}
        </div>
        <div
          className={`${d.teaserArt} ${d.t2Art}`}
          data-hero={design.hero}
          aria-hidden
        />
      </div>
    </div>
  );
}

function TreatmentMobile({
  design,
  onGetCovered,
  onNoThanks,
  bodyFont,
  headerFont,
}: ArmProps) {
  return (
    <div className={d.mobile} style={{ fontFamily: bodyFont }}>
      <div
        className={`${d.teaserArt} ${d.mobileArt}`}
        data-hero={design.hero}
        data-variant={design.layout}
        aria-hidden
      />
      <div className={d.chips}>
        <span className={styles.sponsoredChip}>Sponsored</span>
        {design.layout === 't1' && (
          <span className={styles.sponsoredChip}>For Pet Parents</span>
        )}
      </div>
      <div className={d.mobileHeadRow}>
        <h3 className={d.title} style={{ fontFamily: headerFont }}>
          {design.heading}
        </h3>
        <div className={d.logoCard}>
          <FetchLogo />
        </div>
      </div>
      <p className={d.sub}>{TEASER_SUB}</p>
      {design.trustpilot && <FetchTrustpilot centered />}
      <div className={d.quoteStack}>
        <Button type={ButtonType.button} big onClick={onGetCovered}>
          {MOBILE_CTA_LABEL}
        </Button>
        <p className={d.quoteNote}>{QUOTE_NOTE}</p>
        <button
          type="button"
          className={`${d.decline} ${d.quoteStackDecline}`}
          onClick={onNoThanks}
        >
          No thanks
        </button>
      </div>
    </div>
  );
}

// The ad left in the page after the mobile sheet is closed (Hoi's "inline
// offer" design): a full-width photo strip, then the offer with the two
// buttons side by side. 2.x shows Trustpilot on its own line; its "For pet
// parents" bar is the page panel's header, drawn in index.tsx.
function TreatmentInline({
  design,
  onGetCovered,
  onNoThanks,
  bodyFont,
  headerFont,
}: ArmProps) {
  return (
    <div className={d.inline} style={{ fontFamily: bodyFont }}>
      <div
        className={`${d.teaserArt} ${d.inlineArt}`}
        data-hero={design.hero}
        aria-hidden
      />
      <div className={d.inlineBody}>
        <div className={d.inlineTop} data-layout={design.layout}>
          <div className={d.chips}>
            <span className={styles.sponsoredChip}>Sponsored</span>
            {design.layout === 't1' && (
              <span className={styles.sponsoredChip}>For Pet Parents</span>
            )}
          </div>
          <div className={d.inlineHeadRow}>
            <div className={d.inlineCopy}>
              <h3 className={d.inlineTitle} style={{ fontFamily: headerFont }}>
                {design.heading}
              </h3>
              <p className={d.sub}>{TEASER_SUB}</p>
            </div>
            <div className={d.logoCard}>
              <FetchLogo />
            </div>
          </div>
        </div>
        {design.trustpilot && <FetchTrustpilot />}
        <div className={d.actions}>
          <Button type={ButtonType.button} onClick={onGetCovered}>
            {MOBILE_CTA_LABEL}
          </Button>
          <button type="button" className={d.decline} onClick={onNoThanks}>
            No thanks
          </button>
        </div>
        <p className={`${d.quoteNote} ${d.quoteNoteLeft}`}>{QUOTE_NOTE}</p>
      </div>
    </div>
  );
}

export function FetchTeaser({
  onGetCovered,
  onNoThanks,
  isMobile,
  inline,
}: FetchTeaserProps) {
  const theme = useTheme();
  const design = useFetchDesign();
  const bodyFont = theme.bodytextDefaultNone?.fontFamily;
  const headerFont = theme.headersMediumNone?.fontFamily;

  const armProps = { design, onGetCovered, onNoThanks, bodyFont, headerFont };

  // After the mobile sheet is closed, a treatment shopper finds the same ad,
  // photo included, in the page's panel. Control keeps today's compact panel.
  if (inline && design.layout !== 'control') {
    return <TreatmentInline {...armProps} />;
  }

  if (inline) {
    return (
      <div className={styles.teaserBody} style={{ fontFamily: bodyFont }}>
        <span className={styles.sponsoredChip}>Sponsored</span>
        <div className={styles.teaserRow}>
          <div className={styles.teaserCopy}>
            <h3
              className={styles.teaserTitle}
              style={{ fontFamily: headerFont }}
            >
              {design.heading}
            </h3>
            <p className={styles.teaserSub}>{TEASER_SUB}</p>
          </div>
          <FetchLogo />
        </div>
        <div className={styles.teaserActions}>
          <Button type={ButtonType.button} onClick={onGetCovered}>
            {CTA_LABEL}
          </Button>
          <button
            type="button"
            className={styles.teaserDecline}
            onClick={onNoThanks}
          >
            No thanks
          </button>
        </div>
        <p className={`${d.quoteNote} ${d.quoteNoteLeft}`}>{QUOTE_NOTE}</p>
      </div>
    );
  }

  if (design.layout !== 'control') {
    return isMobile ? (
      <TreatmentMobile {...armProps} />
    ) : (
      <TreatmentDesktop {...armProps} />
    );
  }

  if (isMobile) {
    return (
      <div className={styles.teaserMobile} style={{ fontFamily: bodyFont }}>
        <div className={styles.teaserLogoCard}>
          <FetchLogo />
        </div>
        <div className={styles.teaserCopyCentered}>
          <h3
            className={styles.teaserTitleCentered}
            style={{ fontFamily: headerFont }}
          >
            {design.heading}
          </h3>
          <p className={styles.teaserSubCentered}>{TEASER_SUB}</p>
        </div>
        <div className={d.quoteStack}>
          <Button type={ButtonType.button} big onClick={onGetCovered}>
            {MOBILE_CTA_LABEL}
          </Button>
          <p className={d.quoteNote}>{QUOTE_NOTE}</p>
          <button
            type="button"
            className={`${styles.teaserDeclineLink} ${d.quoteStackDecline}`}
            onClick={onNoThanks}
          >
            No thanks
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.teaserCard} style={{ fontFamily: bodyFont }}>
      <FetchOfferBar />
      <div className={styles.teaserBody}>
        <span className={styles.sponsoredChip}>Sponsored</span>
        <div className={styles.teaserRow}>
          <div className={styles.teaserCopy}>
            <h3
              className={styles.teaserTitle}
              style={{ fontFamily: headerFont }}
            >
              {design.heading}
            </h3>
            <p className={styles.teaserSub}>{TEASER_SUB}</p>
          </div>
          <FetchLogo />
        </div>
        <div className={styles.teaserActions}>
          <Button type={ButtonType.button} onClick={onGetCovered}>
            {CTA_LABEL}
          </Button>
          <button
            type="button"
            className={styles.teaserDecline}
            onClick={onNoThanks}
          >
            No thanks
          </button>
        </div>
        <p className={`${d.quoteNote} ${d.quoteNoteLeft}`}>{QUOTE_NOTE}</p>
      </div>
    </div>
  );
}
